import { Tag } from "@appkit4/react-components";
import { useMemo, useState } from "react";

import {
  type AiInsightsRaidTab,
  type AiInsightsResponse,
  getAiInsightsAvailabilityMessage,
  insightTypeForRaid,
  useAiInsightJob,
} from "../../../api/aiInsights";
import { isApiError } from "../../../api/errors";
import { useGetRaidInsightItems } from "../../../api/raid";
import { AiOutputFeedbackControls } from "../../aiOutputFeedback/AiOutputFeedbackControls";
import { Spinner } from "../../shared";
import {
  AiInsightDeveloperContext,
  AiInsightSourceMarker,
  AiInsightsRaidTopItemCard,
} from "../AiInsightsRaidDetailParts";
import { getTagColor } from "../aiInsightTagColor";
import { AiInsightWaitingState } from "../AiInsightWaitingState";
import { AiInsightsRaidRisksIssuesDetailModal } from "./AiInsightsRaidRisksIssuesDetailModal";
import type {
  RaidRisksIssuesInsightDto,
  RaidRisksIssuesPotentialUnrecordedItemDto,
  RaidRisksIssuesTopItemDto,
} from "./AiInsightsRaidRisksIssuesTypes";

/**
 * PR 8 (minimum scope): this panel always requests the "risks-issues" tab
 * regardless of which RAID sub-tab the user currently has open. Lifting the
 * actually-active tab requires making the shared Tabs component controlled,
 * which is deliberately out of scope for this first pass -- see
 * docs/decision-log.md. Assumptions/Dependencies RAID sub-tabs are not yet
 * wired to real data and remain covered by the static default overlay
 * content until their own follow-up PRs.
 */
const RAID_OVERLAY_TAB: AiInsightsRaidTab = "risks-issues";

/** Backend reason code (Carter.Domain.Exceptions.NotFoundException) surfaced when the
 * current user has no active AI Insights persona assignment. */
const PERSONA_NOT_ASSIGNED_ERROR_CODE = "no_persona";
const RAID_LINKED_ITEM_TYPES = new Set(["risk", "issue", "assumption", "decision"]);

function getRaidLookupIds(items: RaidRisksIssuesTopItemDto[]): string[] {
  return items
    .flatMap((item) => [
      item.item_id,
      ...(Array.isArray(item.linked_items)
        ? item.linked_items
            .filter((linkedItem) =>
              RAID_LINKED_ITEM_TYPES.has(linkedItem.linked_item_type?.toLowerCase() ?? "")
            )
            .map((linkedItem) => linkedItem.linked_item_id)
        : []),
    ])
    .filter((itemId): itemId is string => itemId != null && itemId.trim().length > 0);
}

export type AiInsightsRaidRisksIssuesShelfProps = {
  /** Whether the shared global insights overlay panel is currently open. */
  isOpen: boolean;
  insightResponse?: AiInsightsResponse;
};

/**
 * Renders the real, API-backed "Risks and issues" RAID insight inside the
 * global insights overlay: durable request-and-poll, loading/error states,
 * and inline expected-availability states.
 */
export function AiInsightsRaidRisksIssuesShelf({ isOpen, insightResponse }: AiInsightsRaidRisksIssuesShelfProps) {
  // Only request the panel's job once the overlay is actually open.
  const raidQuery = useAiInsightJob(insightTypeForRaid(RAID_OVERLAY_TAB), isOpen && insightResponse == null);

  // Tracks which single "potential unrecorded risk" candidate card is
  // expanded; index-based since candidates don't carry a stable id.
  const [expandedUnrecordedIndex, setExpandedUnrecordedIndex] = useState<number | null>(null);
  const toggleUnrecordedIndex = (index: number) => {
    setExpandedUnrecordedIndex((current) => (current === index ? null : index));
  };

  // The risk/issue card currently shown in the detail popup modal, if any.
  const [selectedRiskOrIssue, setSelectedRiskOrIssue] = useState<RaidRisksIssuesTopItemDto | null>(
    null
  );

  const raidResponse = insightResponse ?? raidQuery.data?.data;
  const raidInsight = raidResponse?.insight as RaidRisksIssuesInsightDto | undefined;
  const lookupItemIds = useMemo(
    () =>
      getRaidLookupIds(
        Array.isArray(raidInsight?.top_risks_and_issues) ? raidInsight.top_risks_and_issues : []
      ),
    [raidInsight]
  );
  const resolvedRaidItemsQuery = useGetRaidInsightItems(
    lookupItemIds,
    isOpen && raidInsight != null,
    raidResponse?.dealDataVersionId
  );
  const resolvedRaidItemsById = new Map(
    (resolvedRaidItemsQuery.data ?? []).map((item) => [item.itemId.toLowerCase(), item])
  );
  const selectedRiskOrIssueSourceItem = selectedRiskOrIssue?.item_id?.trim()
    ? resolvedRaidItemsById.get(selectedRiskOrIssue.item_id.trim().toLowerCase())
    : undefined;
  const isRaidLoading =
    isOpen && raidQuery.isLoading && raidInsight == null;
  const raidUnderlyingError = raidQuery.error;
  const isPersonaNotAssignedError =
    isApiError(raidUnderlyingError) && raidUnderlyingError.code === PERSONA_NOT_ASSIGNED_ERROR_CODE;
  const isNoDataError = isApiError(raidUnderlyingError) && raidUnderlyingError.code === "no_data";
  const isReadinessWaiting = raidQuery.mappingReason != null;
  const raidError =
    isOpen && !isRaidLoading && raidInsight == null
      ? isPersonaNotAssignedError
        ? "No AI Insights persona is assigned to your account yet."
        : raidQuery.isError && !isNoDataError && !isReadinessWaiting
          ? "Unable to load insights right now. Please try again shortly."
          : null
      : null;
  const raidUnavailableMessage =
    isOpen && !isRaidLoading && raidInsight == null
      ? getAiInsightsAvailabilityMessage(
          isApiError(raidUnderlyingError) ? raidUnderlyingError.code : undefined,
          "Risks and issues"
        )
      : null;

  if (isRaidLoading) {
    return (
      <section
        className="global-insights-overlay__section global-insights-overlay__section--loading"
        aria-live="polite"
      >
        <Spinner size="lg" />
      </section>
    );
  }

  if (raidError != null) {
    return (
      <section className="global-insights-overlay__section" aria-live="assertive">
        <p className="global-insights-overlay__raid-status-text global-insights-overlay__raid-status-text--error">
          {raidError}
        </p>
      </section>
    );
  }

  if (raidUnavailableMessage != null) {
    return isReadinessWaiting ? (
      <AiInsightWaitingState reason={raidQuery.mappingReason} />
    ) : (
      <section className="global-insights-overlay__section" aria-live="polite">
        <p className="global-insights-overlay__raid-status-text">{raidUnavailableMessage}</p>
      </section>
    );
  }

  if (raidInsight == null) {
    return null;
  }

  const topRisksAndIssues = Array.isArray(raidInsight.top_risks_and_issues)
    ? raidInsight.top_risks_and_issues
    : [];
  // The prompt asks the LLM for "up to five" ranked items, but the panel
  // caps rendering at 5 defensively in case the model ever returns more.
  const displayedTopRisksAndIssues = resolvedRaidItemsQuery.isSuccess
    ? topRisksAndIssues
        .filter((item) => {
          const itemId = item.item_id?.trim().toLowerCase();
          return itemId != null && itemId.length > 0 && resolvedRaidItemsById.has(itemId);
        })
        .slice(0, 5)
    : [];
  const potentialUnrecordedRisksOrIssues = Array.isArray(
    raidInsight.potential_unrecorded_risks_or_issues
  )
    ? raidInsight.potential_unrecorded_risks_or_issues
    : [];

  return (
    <>
      <section
        className="global-insights-overlay__section global-insights-overlay__section--raid-health"
        aria-labelledby="global-insights-raid-health-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-raid-health-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Overall risks & issues health
          </h3>
          <AiInsightSourceMarker />
        </div>
        <div className="global-insights-overlay__raid-health-card">
          <p className="global-insights-overlay__raid-health-text">
            {raidInsight.headline ?? "Not available"}
          </p>
        </div>
        {raidResponse?.status === "ready" && (
          <AiOutputFeedbackControls
            executionId={raidResponse.aiOutputExecutionId}
            outputType="ai_insight"
            className="global-insights-overlay__feedback"
          />
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-raid-drivers-title"
      >
        <div className="global-insights-overlay__section-header">
          <h3
            id="global-insights-raid-drivers-title"
            className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
          >
            Top risks & issues requiring attention
          </h3>
        </div>

        {displayedTopRisksAndIssues.length > 0 ? (
          <div className="global-insights-overlay__raid-driver-list">
            {displayedTopRisksAndIssues.map((item) => {
              const itemId = item.item_id?.trim();
              const sourceItem = itemId
                ? resolvedRaidItemsById.get(itemId.toLowerCase())
                : undefined;
              if (!itemId || !sourceItem) return null;

              return (
                <AiInsightsRaidTopItemCard
                  key={itemId}
                  itemId={sourceItem.itemId}
                  title={sourceItem.title ?? "Risk or issue detail"}
                  owner={sourceItem.owner}
                  primaryMeta={sourceItem?.raidType}
                  secondaryMeta={sourceItem?.workstream}
                  aiGenerated
                  onOpen={() => setSelectedRiskOrIssue(item)}
                />
              );
            })}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No top risks and issues available.
          </p>
        )}
      </section>

      <section
        className="global-insights-overlay__section"
        aria-labelledby="global-insights-raid-unrecorded-title"
      >
        <h3
          id="global-insights-raid-unrecorded-title"
          className="global-insights-overlay__section-title global-insights-overlay__section-title--raid"
        >
          Potential unrecorded risks
        </h3>

        {potentialUnrecordedRisksOrIssues.length > 0 ? (
          <div className="global-insights-overlay__raid-unrecorded-list">
            {potentialUnrecordedRisksOrIssues.map((candidate, index) => (
              <UnrecordedRiskCard
                key={candidate?.suggested_title ?? `potential-unrecorded-${index}`}
                candidate={candidate}
                isExpanded={expandedUnrecordedIndex === index}
                onToggle={() => toggleUnrecordedIndex(index)}
              />
            ))}
          </div>
        ) : (
          <p className="global-insights-overlay__raid-status-text">
            No potential unrecorded risks or issues available.
          </p>
        )}
      </section>

      <AiInsightDeveloperContext developerContext={{ asAt: raidInsight.as_at, dataUsed: raidInsight.context_summary?.data_used, missingInputs: raidInsight.context_summary?.data_missing_or_restricted, limitations: raidInsight.data_quality_and_caveats }} />

      {selectedRiskOrIssue != null && selectedRiskOrIssueSourceItem != null && (
        <AiInsightsRaidRisksIssuesDetailModal
          item={selectedRiskOrIssue}
          sourceItem={selectedRiskOrIssueSourceItem}
          asAtDate={raidInsight.as_at ?? raidResponse?.asAt}
          resolvedItemsById={resolvedRaidItemsById}
          onClose={() => setSelectedRiskOrIssue(null)}
        />
      )}
    </>
  );
}

type UnrecordedRiskCardProps = {
  /** A single "potential unrecorded risk or issue" candidate returned by the LLM. */
  candidate: RaidRisksIssuesPotentialUnrecordedItemDto;
  /** Whether this candidate's collapsible detail body is currently expanded. */
  isExpanded: boolean;
  /** Toggles the expanded state; only one candidate is expanded at a time. */
  onToggle: () => void;
};

/**
 * Collapsible card for a single "potential unrecorded risk or issue"
 * candidate. Renders a summary header (candidate type, confidence, and
 * suggested title) that is always visible, with the fuller LLM-provided
 * rationale (why flagged, possible impact, validation question, and any
 * possible existing RAID match) revealed on expand.
 */
export function UnrecordedRiskCard({ candidate, isExpanded, onToggle }: UnrecordedRiskCardProps) {
  const evidenceIds = Array.isArray(candidate.evidence_ids) ? candidate.evidence_ids : [];
  const possibleMatch = candidate.possible_existing_raid_match?.trim();
  const possibleMatchIds = [...new Set(possibleMatch?.match(/\b[A-Z]+-\d+\b/g) ?? [])];
  const workstream = candidate.likely_relevant_workstream?.trim();

  return (
    <div className="global-insights-overlay__raid-unrecorded-card global-insights-overlay__raid-unrecorded-card--ai-generated">
      <AiInsightSourceMarker />
      <button
        type="button"
        className="global-insights-overlay__raid-unrecorded-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="global-insights-overlay__raid-unrecorded-summary">
          <p className="global-insights-overlay__raid-unrecorded-meta">
            <span>{candidate?.candidate_type ?? "Not available"}</span>
            <span className="global-insights-overlay__raid-unrecorded-confidence">
              {`Confidence: ${candidate?.confidence ?? "Not available"}`}
            </span>
          </p>
          <h4 className="global-insights-overlay__raid-unrecorded-title">
            {candidate?.suggested_title ?? "Not available"}
          </h4>
        </div>
        <span
          className={[
            "global-insights-overlay__raid-unrecorded-chevron",
            isExpanded ? "global-insights-overlay__raid-unrecorded-chevron--expanded" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden="true"
        >
          <span className="Appkit4-icon icon-down-chevron-outline" />
        </span>
      </button>

      {isExpanded && (
        <div className="global-insights-overlay__raid-unrecorded-content">
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate?.why_flagged ?? "Not available"}
          </p>

          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Possible impact</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate?.possible_impact ?? "Not available"}
          </p>

          <hr className="global-insights-overlay__raid-unrecorded-divider" />
          <p className="global-insights-overlay__raid-unrecorded-label">Validation question</p>
          <p className="global-insights-overlay__raid-unrecorded-text">
            {candidate?.validation_question ?? "Not available"}
          </p>

          {(possibleMatchIds.length > 0 || workstream != null) && (
            <>
              <hr className="global-insights-overlay__raid-unrecorded-divider" />
              <div className="global-insights-overlay__raid-unrecorded-tags">
                {possibleMatchIds.length > 0 && (
                  <>
                    <span className="global-insights-overlay__raid-unrecorded-tags-label">
                      Possible matches:
                    </span>
                    {possibleMatchIds.map((possibleMatchId) => (
                      <Tag
                        key={possibleMatchId}
                        label={possibleMatchId}
                        type="outlined"
                        size="sm"
                        closable={false}
                        color={getTagColor(possibleMatchId)}
                      />
                    ))}
                  </>
                )}
                {workstream != null && (
                  <Tag
                    label={workstream}
                    type="outlined"
                    size="sm"
                    closable={false}
                    color={getTagColor(workstream)}
                  />
                )}
              </div>
            </>
          )}
          {evidenceIds.length > 0 && (
            <p className="global-insights-overlay__raid-unrecorded-text">{`Evidence: ${evidenceIds.join(", ")}`}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default AiInsightsRaidRisksIssuesShelf;
